import {Component, OnInit, ViewChild, ElementRef, AfterViewInit, inject, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PlansService} from 'src/app/services/plans.service';
import {PracticeInterface, PracticesDirectoryRequestInterface} from 'src/app/interfaces/plans.interface';
import {HttpError} from 'src/app/models/http-error.model';
import {environment} from 'src/environments/environment';
import {EventsManager} from 'src/app/services/events.manager';
import {GoogleMap, MapAdvancedMarker, MapInfoWindow} from '@angular/google-maps';
import {GoogleAnalyticsService} from 'src/app/services/google-analytics.service';
import {PhonePipe} from 'src/app/pipes/phone.pipe';
import {LocationCardComponent} from 'src/app/components/location-card/location-card.component';
import {NgIf, NgFor} from '@angular/common';
import {ActivatedRoute} from '@angular/router';

@Component({
  selector: 'app-find-a-dentist',
  templateUrl: './find-a-dentist.component.html',
  styleUrl: './find-a-dentist.component.scss',
  imports: [NgIf, NgFor, LocationCardComponent, GoogleMap, MapAdvancedMarker, MapInfoWindow, PhonePipe]
})

export class FindADentistComponent implements OnInit, AfterViewInit {
  @ViewChild('searchBox') public searchBox!: ElementRef;
  @ViewChild(GoogleMap, {static: false}) public mapContainer!: GoogleMap;
  @ViewChild(MapInfoWindow) public infoWindow!: MapInfoWindow;

  private _plansService = inject(PlansService);
  private _eventsManager = inject(EventsManager);
  private _googleAnalyticsService = inject(GoogleAnalyticsService);
  private _activatedRoute = inject(ActivatedRoute);
  private _destroyRef = inject(DestroyRef);

  public markers: Array<{ position: google.maps.LatLngLiteral; label: string; practice: PracticeInterface, content: any }> = [];
  public infoContent: string = '';
  public iconColor: string = '#FDB936';

  public showResults: boolean = false;
  public autocomplete!: google.maps.places.Autocomplete;
  public originalPractices!: PracticeInterface[];
  public filteredPractices!: PracticeInterface[];

  public center: google.maps.LatLngLiteral = {lat: 39.4515876, lng: -97.1658519};
  public zoom = 5;
  public mapId: string = environment.googleMapId;
  public mapOptions: google.maps.MapOptions = {
    mapId: this.mapId,
    zoomControl: true,
    zoomControlOptions: {
      position: google.maps.ControlPosition.RIGHT_BOTTOM
    },
    maxZoom: 18,
    disableDefaultUI: true,
    mapTypeId: google.maps.MapTypeId.ROADMAP,
    mapTypeControl: false,
    streetViewControl: false
  };
  public map!: google.maps.Map;

  public ngOnInit() {
    this.initializeAddress();

    this._eventsManager.planDataUpdated
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((data: string) => {
        this.getDirectory(data);
      });

    const url: string = this._activatedRoute.snapshot.url.join('|');

    if (url.includes('adha')) {
      this._eventsManager.purchaseIsADHA = true;
      this._eventsManager.setHideHeader(true);
    }
  }

  public ngAfterViewInit(): void {
    window.scroll(0, -window.scrollY);
    this.map = this.mapContainer.googleMap!;
    this.addBoundsChangeListener();
    this.initializeAutocomplete();

    const mapContainer = document.querySelector('#mapContainerDiv');
    if (mapContainer) {
      if (mapContainer) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
              const infoWindowContent = mapContainer.querySelector('.gm-style-iw-d');
              if (infoWindowContent) {
                (infoWindowContent as HTMLElement).style.overflow = 'hidden !important';
                observer.disconnect();
              }
            }
          });
        });

        observer.observe(mapContainer, {
          childList: true,
          subtree: true
        });
      }
    }
  }

  public addBoundsChangeListener() {
    if (this.map) {
      this.map.addListener('bounds_changed', () => {
        this.onBoundsChanged();
      });
    }
  }

  public initializeAddress(): void {
    let planeName: string = '';
    if (this._eventsManager && this._eventsManager.planName != '') {
      planeName = this._eventsManager.planName;
    }
    this.getDirectory(planeName);
  }

  public initializeAutocomplete() {
    this.autocomplete = new google.maps.places.Autocomplete(
      this.searchBox.nativeElement,
      {types: ['geocode'], componentRestrictions: {country: 'us'}}
    );

    this.autocomplete.addListener('place_changed', () => {
      const place = this.autocomplete.getPlace();
      if (place.geometry) {
        const location = place.geometry.location!;
        this.map.setCenter(location);
        this.map.setZoom(10);
      }
    });
  }

  public getDirectory(name: string) {
    const request: PracticesDirectoryRequestInterface = {
      planName: name
    };
    this._plansService.getPracticesDirectory(request)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result) => {
        if (!(result instanceof HttpError)) {
          this.originalPractices = result.entries;
          this.onBoundsChanged();
        }
      });
  }

  public findLocations() {
    if (this.autocomplete.getPlace()) {
      this.onBoundsChanged();
    }
  }

  public clearMarkers() {
    this.showResults = false;
    this.filteredPractices = [];
    this.markers = [];
  }

  public createMarkers() {
    if (!this.filteredPractices || this.filteredPractices.length === 0) {
      return;
    }

    this.markers = [];
    this.filteredPractices.forEach((practice, index) => {
      this.markers.push({
        position: {lat: practice.latitude!, lng: practice.longitude!}, // Make sure latitude and longitude are defined
        label: `${index + 1}`,
        practice,
        content: this.getMarkerContent(index)
      });
    });
    this.showResults = true;
  }

  public onMarkerClick(advancedMarker: MapAdvancedMarker, marker: { position: google.maps.LatLngLiteral; practice: PracticeInterface, content: any }) {
    if (this.infoWindow.infoWindow!.isOpen) {
      this.infoWindow.close();
    }
    this.infoContent = this.getInfoWindowContent(marker.practice);
    this.infoWindow.openAdvancedMarkerElement(advancedMarker.advancedMarker, this.infoContent);
    // @ts-ignore
    const elem: number = document.getElementById('marker' + marker.label).offsetTop;
    // @ts-ignore
    const cont: number = document.getElementById('dentist-listings').offsetTop;
    // @ts-ignore
    document.getElementById('dentist-listings').scrollTo({top:elem-cont, behavior: 'smooth'});
  }

  public getInfoWindowContent(practice: PracticeInterface): string {
    let site = '';
    if (practice.url) {
      site = `
      <br/><br/>
      <a href="${practice.url}" target=_blank>
        <i class="bi bi-link-45deg icon" style="margin-right: 5px;"></i>Website
      </a>`;
    }

    const phone = this.formatToPhoneNumber(practice.phone);

    const addressFull =
      practice.address.line1 +
      (practice.address.line2 ? ' ' + practice.address.line2 : '') +
      '<br />' +
      practice.address.city +
      ', ' +
      practice.address.state +
      ' ' +
      practice.address.zipCode;

    const infoWindowContent = `
      <div style="font-weight: bold; font-size: 1.2rem;color: #5a3ec8;"> ${practice.title}</div>
      <div style="font-size: 14px;">${addressFull}</div><hr>
      <div style="color: #5a3ec8; font-size: 14px;">
        <a href='tel:${practice.phone}'>${phone}</a>
        <a href="http://maps.google.com/?q=${addressFull.replace('<br />', ', ')}" style="padding-left:10px;" target=_blank>
          <i class="bi bi-geo-alt icon" style="margin-right: 5px;"></i>Get Directions
        </a>
        ${site}
      </div>
    `;
    return infoWindowContent;
  }

  public formatToPhoneNumber(value: string): string {
    const cleaned = value.replace(/\D/g, '');

    if (cleaned.length !== 10) {
      return 'Invalid phone number';
    }

    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  public getMarkerContent(index: number): any {
    const iconNumber = index + 1;
    const markerElement = document.createElement('div');
    markerElement.classList.add('drop-animation');
    markerElement.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="45px" height="63px" viewBox="0 0 39 54">
        <g fill="${this.iconColor}" fill-rule="evenodd">
          <g transform="translate(-805.000000, -662.000000)">
            <g transform="translate(805.000000, 662.000000)">
              <g>
                <g transform="translate(10.000000, 10.000000)" fill="white" stroke="%23979797">
                  <circle cx="9.5" cy="9.5" r="9.5"/>
                </g>
                <g fill="${this.iconColor}" text-anchor="middle" fill-rule="nonzero">
                  <path d="M28.7368421,19.7307692 C28.7368421,24.8906788 24.60136,29.0769231 19.5,29.0769231 C14.3979138,29.0769231 10.2631579,24.8906788 10.2631579,19.7307692 C10.2631579,14.5693833 14.39864,10.3846154 19.5,10.3846154 C24.6024493,10.3842463 28.7368421,14.5690142 28.7368421,19.7307692 M39,19.6364694 C39,8.79133788 30.2700926,0 19.5,0 C8.73067398,0 0,8.79133788 0,19.6364694 C0,24.4047446 1.68948641,28.7767622 4.49699275,32.1775218 L19.5168642,54 L34.7563536,31.8591974 C35.3036736,31.1694298 35.8050003,30.4424404 36.2572675,29.6782291 L36.411345,29.4556734 L36.3860487,29.4556734 C38.0456395,26.5655511 39,23.2148083 39,19.6364694"/>
                </g>
                <text x="19.1" y="24" font-family="Sans-Serif" font-size="12" font-weight="bold" fill="${this.iconColor}" text-anchor="middle">${iconNumber}</text>
              </g>
            </g>
          </g>
        </g>
      </svg>`;
    return markerElement;
  }

  public onBoundsChanged() {
    const bounds = this.mapContainer.googleMap!.getBounds();
    if (bounds && this.originalPractices && this.originalPractices.length > 0) {
      this.clearMarkers();
      const zoomLevel = this.map.getZoom();
      if (zoomLevel && zoomLevel >= 10) {
        this.originalPractices.forEach(practice => {
          if (this.map.getBounds() &&
            this.map.getBounds()!.contains({
              lat: practice.latitude!,
              lng: practice.longitude!
            })
          ) {
            this.filteredPractices.push(practice);
          }
        });
        this.createMarkers();
      }
    }
  }

  public clickDentistWebsite() {
    this._googleAnalyticsService.sendKeyEvent('click_dentist_website', {
      timestamp: new Date().toISOString()
    });
  }
}
